import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Users, Lock, Flame, Loader2, Link2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DuoCuriosite() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const isCocon = !!(user && (user.role === "admin" || user.plan === "cocon"));

  const [topic, setTopic] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { id, link }

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/duo/create", { topic: topic.trim(), lang });
      localStorage.setItem(`coccon_duo_${data.id}`, JSON.stringify({ pid: data.participant_id, name: data.name }));
      const link = `${window.location.origin}/duo/${data.id}`;
      setCreated({ id: data.id, link });
    } catch (e) {
      if (e.response?.status === 402) toast.error(t("atelier.duoLocked"));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(created.link); } catch { /* ignore */ }
    toast.success(t("atelier.duoLinkCopied"));
  };

  if (!isCocon) {
    return (
      <div className="h-full overflow-y-auto scroll-thin">
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <div className="glass rounded-3xl p-10 frame-pulse" data-testid="duo-locked">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <Lock className="text-primary" size={26} />
            </div>
            <h2 className="font-display text-3xl text-gradient-orange mb-4">{t("atelier.duoLocked")}</h2>
            <p className="text-foreground/60 leading-relaxed mb-8">{t("atelier.duoLockedDesc")}</p>
            <button
              data-testid="duo-upgrade-btn"
              onClick={() => navigate("/tarifs")}
              className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
            >
              <Sparkles size={17} /> {t("atelier.upgrade")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-2xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 justify-center mb-3">
          <Users className="text-primary" size={22} />
          <span className="uppercase tracking-[0.22em] text-xs text-primary/90">{t("atelier.duoRoomEyebrow")}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-gradient-orange text-center leading-tight mb-4">{t("atelier.duoTitle")}</h1>
        <p className="text-foreground/60 text-center leading-relaxed mb-12 max-w-lg mx-auto">{t("atelier.duoSubtitle")}</p>

        {!created ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 frame-pulse" data-testid="duo-create-card">
            <label className="block text-sm text-foreground/70 mb-3">{t("atelier.duoTopicLabel")}</label>
            <textarea
              data-testid="duo-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={2}
              placeholder={t("atelier.duoTopicPlaceholder")}
              className="w-full bg-transparent border border-white/15 rounded-2xl px-4 py-3 outline-none text-foreground placeholder:text-foreground/30 resize-none focus:border-primary/50 transition-colors"
            />
            <button
              data-testid="duo-create-btn"
              onClick={create}
              disabled={creating}
              className="btn-frost-orange w-full mt-6 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base disabled:opacity-50"
            >
              {creating ? <Loader2 size={18} className="animate-spin" /> : <Flame size={18} />}
              {creating ? t("atelier.duoCreating") : t("atelier.duoCreate")}
            </button>
            <p className="text-xs text-foreground/40 text-center mt-5 leading-relaxed">{t("atelier.duoHint")}</p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 frame-pulse text-center" data-testid="duo-created-card">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <Flame className="text-primary" size={26} />
            </div>
            <p className="text-foreground/70 leading-relaxed mb-6">{t("atelier.duoInvite")}</p>
            <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-full px-4 py-2.5 mb-6">
              <input
                data-testid="duo-link-field"
                readOnly
                value={created.link}
                className="flex-1 bg-transparent text-sm text-foreground/70 outline-none truncate"
              />
              <button data-testid="duo-copy-btn" onClick={copyLink} className="text-primary hover:text-primary/80 transition-colors flex-shrink-0" title={t("atelier.duoShareLink")}>
                <Link2 size={18} />
              </button>
            </div>
            <button
              data-testid="duo-enter-btn"
              onClick={() => navigate(`/duo/${created.id}`)}
              className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base"
            >
              {t("atelier.duoOpen")} <ArrowRight size={18} />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
